import React, { useMemo, useState, useEffect, useContext, useRef } from 'react'
import { useInfiniteQuery, useQuery, useQueryClient } from 'react-query'
import cookies from 'next-cookies'
import Cookie from 'js-cookie'
import { TFunction } from 'next-i18next'
import Octicon, { Plus, FileSymlinkFile } from '@primer/octicons-react'
import { Button } from '@viadelivery/design-system'
import { useToasts } from 'react-toast-notifications'
import { useAtom } from 'jotai'
import { useBXWidgetControl } from '~/utils/widgetHook'
import { useRouter } from 'next/router'

import { withTranslation } from '~/i18n'
import { getData, postData } from '~/utils/api'
import { getItemsCount } from '~/utils/helpers'
import CarrierFilter from '~/components/OrdersPage/CarrierFilter'
import DatePicker from '~/components/OrdersPage/DatePicker'
import ChartStatus from '~/components/OrdersPage/ChartStatus'
import ChartDate from '~/components/OrdersPage/ChartDate'
import GetOrderInfo from '~/components/OrderInfo/GetOrderInfo'
import ModalComponent from '~/components/OrdersPage/Modal'
import OnAction from '~/utils/OnAction'
import Switch from '~/components/Switch/Switch'
import Timezones from '~/components/OrdersPage/Timezones'
import styles from '~/styles/orders.module.css'

import {
  DATES_RANGE,
  LOCALE,
  ORDER,
  STATUS_OPTION,
  STATUS,
  ORDER_DETAILS,
} from '~/types'
import {
  DateRange,
  Layout,
  Modal,
  OrderDetails,
  SearchInput,
  Select,
  Table,
  TableSettingsDropdown,
  Map,
  SessionContext,
  AdBanner
} from '../../components'
import ExportTo from '../../components/OrdersPage/ExportTo'
import { QUERY_TYPES, ALL_ORDERS_COLUMNS, ALL_ORDERS_COLUMNS_EN, DEFAULT_SHOWED_COLUMNS } from '../../constants'
import {
  catchFocusAtom,
  modalAtom,
  modalDeliveryAddressAtom,
} from '../../states'

import { startAnimation } from 'framer-motion'
import e from 'express'

const { ORDERS, ORDER_INFO } = QUERY_TYPES

const useOuterClick = (setIsOuterClicked, clickedComponentInside, setClickedComponent) => {
	useEffect(() => {
		const handleClick = (e) => {
			const date = (typeof clickedComponentInside === 'number' ? clickedComponentInside : 0)
			const now = Date.now()
			if ((now - date) > 300) {
				setIsOuterClicked?.(now)
			} else {
				setIsOuterClicked?.(0)
			}
		}
		document.addEventListener("click", handleClick)
		return () => {
			document.removeEventListener("click", handleClick)
		}
	}, [setIsOuterClicked, clickedComponentInside])
}

let externalEventCallbacks = {}
let fetchedOrders = {}
let fetchedOrderInfos = {}
let isFetching = false
let startDateRange = {}
let start = Date.now()

const syntheticsDealers = [
  "syntheticIdForManagersCommonUsDealer",
  "syntheticIdForManagersCommonGbDealer",
  "35877652-9900-4767-b64b-8214dac5e848" // Via.Delivery Russia
]
let preventDevExtraRequestsObject = {}
let datePickerApply = null
const setDatePickerApply = (callback) => {
  datePickerApply = callback
}
let fetchingOrdersTO = null
let areOrdersFetched = false
let isOrderModalUntouched = null
let updatePoints = null
let search = ''
const setUpdatePoints = (callback) => {
  updatePoints = callback
}
let orderListOffset = 0
let searchST
let orderInfos = {}

interface Props {
  dateRange: DATES_RANGE
  locale?: LOCALE
  orders: ORDER[]
  orderStatus?: STATUS | string
  searchTerm?: string
  session: string
  statuses?: STATUS_OPTION[]
  t: TFunction
}

const pageCount = 30
const defaultOrder = {
  id: 0,
  ext_key: '',
  paid: false,
  service_name: '',
  index: 0,
}

let stage = 'prod'
if (typeof window !== 'undefined') {
  const host = window.location.host
  if (host.includes('localhost')) {
    stage = 'localhost'
  } else if (host.includes('stage')) {
    stage = 'stage'
  }
}


const Orders = (props: Props) => {
  const {
    dateRange,
    locale = 'en',
    orderStatus = '',
    searchTerm = '',
    session,
    statuses = [],
    dealerCurrency,
    t,
    i18n,
  } = props
  const now = Date.now()
  Cookie.set('href', '/shop/orders')

  const browserTimezoneOffset = new Date().getTimezoneOffset() * -1
  const browserTimezone = browserTimezoneOffset / 60

  const router = useRouter()
  const { addToast } = useToasts()
  const { currentDealer, dealersObject } = useContext(SessionContext)
  const onAction = new OnAction(currentDealer?.uuid, currentDealer?.name)
  const enCountries = ['US', 'GB', 'UK', 'CA']
  const isEnDealer = enCountries.includes(currentDealer?.dealer_country)
  if (isEnDealer) {
    if (i18n.language !== 'en') {
      i18n.changeLanguage('en')
    }
  } else if (i18n.language !== locale) {
    i18n.changeLanguage(locale)
  }
  const [isCatchFocusEnabled, setCatchFocus] = useAtom(catchFocusAtom)
  const [isModalOpen, setModalIsOpen] = useAtom(modalAtom)
  const [isModalDeliveryOpen, setModalDeliveryIsOpen] = useAtom(
    modalDeliveryAddressAtom,
  )
  const [datesFilter, setDatesFilter] = useState(startDateRange?.startDate ? startDateRange : dateRange)
  const [searchFilter, setSearchFilter] = useState(searchTerm || '')
  const [searchString, setSearchString] = useState(searchTerm || '')
  const [orderStatusFilter, setOrderStatusFilter] = useState(orderStatus)
  const [selectedOrder, setSelectedOrder] = useState(defaultOrder)
  const queryClient = useQueryClient()
  const [ shownColumns, setShownColumns ] = useState(DEFAULT_SHOWED_COLUMNS)
  let allColumns = ALL_ORDERS_COLUMNS()
  if (i18n.language === 'en') {
    allColumns = ALL_ORDERS_COLUMNS_EN()
  }
  const [backUrl, setBackUrl] = useState('')
  const [ hint, setHint ] = useState({})
	const [ clickedComponentInside, setClickedComponent ] = useState(null)
	const [ isOuterClicked, setIsOuterClicked ] = useState(Date.now())
	useOuterClick(setIsOuterClicked, clickedComponentInside, setClickedComponent)
	const [ clickedOrder, setClickedOrder ] = useState(0)
  const [ lastDetailedOrder, setLastDetailedOrder ] = useState(0)
  const [ ordersWithShownItems, setOrdersWithShownItems ] = useState({})
	const [ onCloneData, setOnCloneData ] = useState({})
	const [ newOrder, setNewOrder ] = useState({})
	const [ editOrder, setEditOrder ] = useState({})
	const [ isModalComponentOpen, setIsModalComponentOpen ] = useState(false)
  const [ isOrderModalUntouchedState, setIsOrderModalUntouched ] = useState(isOrderModalUntouched)
  const [ clickLabel, setClickLabel ] = useState(false)
  const [ chartDate, setChartDate ] = useState(null)
  const [ chartEndDate, setChartEndDate ] = useState(null)
  const [ chartIntervalType, setChartIntervalType ] = useState(null)
  const [ chartStatus, setChartStatus ] = useState(null)
  const [ chartCityOrRegion, setChartCityOrRegion ] = useState(null)
  const [ chartIsRegion, setChartIsRegion ] = useState(null)
  const [ chartNpsType, setChartNpsType ] = useState(null)
  const [ chartBopa, setChartBopa ] = useState(null)
  const [ chartBopaMode, setChartBopaMode ] = useState(null)
  const [ chartDayStratification, setChartDayStratification ] = useState(null)
  const [ chartDeliveryReturnType, setChartDeliveryReturnType ] = useState(null)
  const [ chartNpsPerIntervalType, setChartNpsPerIntervalType ] = useState(null)
  const [ showHierarchy, setShowHierarchy ] = useState(props.isHierarchyShown || true)
  const [ showHierarchyInitiaror, setShowHierarchyInitiaror ] = useState(null)
  const [ isHierarchyBeenClicked, setIsHierarchyBeenClicked ] = useState(false)
  const [ isOrdersFetching, setIsOrdersFetching ] = useState(false)
  const [ orderDetailsStyle, setOrderDetailsStyle ] = useState(null)
  const [ ordersFrameStyle, setOrdersFrameStyle ] = useState(null)
  const orderDetailsCounterpartRef = useRef()
  const [ orderStatuses, setOrderStatuses ] = useState(statuses)
  const [ currentPage, setCurrentPage ] = useState('orders')
  const [ orderListUpdater, setOrderListUpdater ] = useState(0)
  const [ timezoneCode, setTimezoneCode ] = useState(null)
  const [ timezoneName, setTimezoneName ] = useState(null)
  const [ isHoursAndMinutesShown, setIsHoursAndMinutesShown ] = useState(false)
  const [ sortingPickerType, setSortingPickerType ] = useState(null)
  const [ datePickerColumn, setDatePickerColumn ] = useState(null)
  const [ isDatePickerSet, setIsDatePickerSet ] = useState(false)

  useEffect(() => {
    const areOrderDetailsToBeHide = localStorage('hideOrdersDetails')
    const originHeaderLink = localStorage('originHeaderLink')
    if (areOrderDetailsToBeHide || (originHeaderLink + '').includes('dashboard')) {
      closeDetails()
    } else {
      showOrdersDetails()
    }
    const isTimeShown = !!(localStorage('isTimeShown'))
    setIsHoursAndMinutesShown(isTimeShown)
    const lsSortingPickerType = localStorage('sortingPicker')
    setSortingPickerType(lsSortingPickerType || 'orderCreated')
    const lsDatePickerColumn = localStorage('datePickerColumn')
    setDatePickerColumn(lsDatePickerColumn || 'orderCreated')
    const lsShownColumns = localStorage('shownColumns')
    if (lsShownColumns) {
      setShownColumns(lsShownColumns)
    }
    if (typeof window !== 'undefined') {
      let urlDealerId = null
      const searchString = (window.location.search + '').replace(/^\?/, '')
      const parameters = searchString.split('&')
      parameters.forEach(parameter => {
        const keyValue = (parameter + '').split('=')
        if (keyValue[0] === 'dealerId') {
          urlDealerId = keyValue[1]
        }
      })
      if (urlDealerId) {
        Cookie.set('currentDealer', urlDealerId)
        window.location.href = window.location.origin + window.location.pathname
      }
      setBackUrl(window.location.href + '&dealerId=' + currentDealer.id)
      try {
        let chartToOrderListFromLocalStorage = window?.localStorage?.getItem('chartToOrderList')
        const chartToOrder = JSON.parse(chartToOrderListFromLocalStorage)
        const chartDate = chartToOrder?.date
        if (chartDate?.length === 10) {
          setChartDate(chartDate)
          if (chartToOrder?.intervalType && chartToOrder?.intervalType !== 'day') {
            setChartIntervalType(chartToOrder?.intervalType)
          }
        }
        setChartEndDate(chartToOrder?.endDate)
        setChartStatus(chartToOrder?.status)
        setChartCityOrRegion(chartToOrder?.cityOrRegion)
        setChartIsRegion(chartToOrder?.isRegion)
        setChartNpsType(chartToOrder?.npsType || null)
        setChartBopa(chartToOrder?.chartBopa || null)
        setChartBopaMode(chartToOrder?.chartBopaMode || null)
        setChartDayStratification(chartToOrder?.chartDayStratification || null)
        setChartDeliveryReturnType(chartToOrder?.chartDeliveryReturnType || null)
        setChartNpsPerIntervalType(chartToOrder?.npsPerIntervalType || null)
      } catch(e) {}
    }
  }, [])

	const onNewOrder = (order) => {
		setNewOrder(order)
		setEditOrder({})
    isOrderModalUntouched = 'onNewOrder'
    setIsOrderModalUntouched(isOrderModalUntouched)
		setTimeout(() => {
			setIsModalComponentOpen(true)
		})
	}
	const onEditOrder = (order) => {
		setEditOrder(order)
		setNewOrder({})
		setIsModalComponentOpen(true)
    isOrderModalUntouched = 'onEditOrder'
    setIsOrderModalUntouched(isOrderModalUntouched)
	}
	const editTheOrder = (key, value) => {
    isOrderModalUntouched = 'editTheOrder'
    setIsOrderModalUntouched(isOrderModalUntouched)
		const isNewOrder = (editOrder?.onEdit ? false : true)
    const order = isNewOrder ? newOrder : editOrder
		let orderCopy = JSON.parse(JSON.stringify(order))
		if (typeof key === 'object') {
			Object.keys(key).forEach(orderKey => {
				orderCopy[orderKey] = key[orderKey]
			})
		} else if (typeof key === 'string') {
			if (key === 'onOrderItemsChange') {
				orderCopy = value
			} else if (key === 'in_cust_phone' && value === '+') {
				orderCopy[key] = ''
			} else {
				orderCopy[key] = value
			}
		}
		if (isNewOrder) {
			setNewOrder(orderCopy)
		} else {
			setEditOrder(orderCopy)
		}
	}

	const setOnExternalEventCallback = (name, callback) => {
			externalEventCallbacks[name] = callback
	}

	const onModalClose = () => {
		setNewOrder({})
	}

  useEffect(() => {
    if (isDatePickerSet) {
      queryClient.invalidateQueries(ORDERS)
      queryClient.removeQueries(ORDERS)
      setSelectedOrder(defaultOrder)
    }
  }, [
    datesFilter,
    orderStatusFilter,
    searchString,
    chartStatus,
    chartNpsType,
    chartBopa,
    chartBopaMode,
    chartDayStratification?.isRetention,
    chartDayStratification?.day,
    chartDayStratification?.part,
    chartDeliveryReturnType,
    chartNpsPerIntervalType,
    orderListUpdater,
    sortingPickerType,
    datePickerColumn,
    props.appState.carrierFilter,
  ])

  const fetchOrders = ({ pageParam = 0 }) => {
    return new Promise(async (resolve, reject) => {
      if (isDatePickerSet) {
        const isStartDateUpdated = startDateRange.startDate === datesFilter?.startDate
        const isEndDateUpdated = startDateRange.endDate === datesFilter?.endDate
        const isRightDates = ((isStartDateUpdated && isEndDateUpdated) || !startDateRange.startDate)
        let options = {
          ...datesFilter,
          orderStatusFilter,
          searchFilter: searchString,
          limit: pageCount,
          offset: pageParam || null,
          browserTimezone,
          showHierarchy,
          timezoneCode,
          datePickerColumn,
        }
        if (enCountries.includes(props.currentDealer.dealer_country)) {
          options.carrierFilter = props.appState.carrierFilter?.toUpperCase()
        }
        const isOrdersPage = (currentPage === 'orders')
        if (isOrdersPage) {
          const now = Date.now()
          let chartDate = null

          options.chartStatus = chartStatus
          options.chartCityOrRegion = chartCityOrRegion
          options.chartIsRegion = chartIsRegion
          options.chartBopaMode = chartBopaMode
          options.chartBopa = chartBopa
          options.chartBopaMode = chartBopaMode
          options.chartNpsType = chartNpsType
          options.chartDayStratification = chartDayStratification
          options.chartDeliveryReturnType = chartDeliveryReturnType
          options.chartNpsPerIntervalType = chartNpsPerIntervalType

          let chartToOrderListFromLocalStorage = window?.localStorage?.getItem('chartToOrderList')
          if (chartToOrderListFromLocalStorage) {
            try {
              const chartToOrder = JSON.parse(chartToOrderListFromLocalStorage)
              chartDate = chartToOrder?.date
              if (chartDate?.length === 10) {
                setChartDate(chartDate)
                if (chartToOrder?.intervalType && chartToOrder?.intervalType !== 'day') {
                  setChartIntervalType(chartToOrder?.intervalType)
                }
              }
              setChartEndDate(chartToOrder?.endDate)

              if (chartToOrder.hasOwnProperty('chartStatus')) {
                const value = chartToOrder?.chartStatus || null
                setChartStatus(value)
                options.chartStatus = value
              }
              if (chartToOrder.hasOwnProperty('cityOrRegion')) {
                const value = chartToOrder?.cityOrRegion || null
                setChartCityOrRegion(value)
                options.cityOrRegion = value
              }
              if (chartToOrder.hasOwnProperty('chartIsRegion')) {
                const value = chartToOrder?.chartIsRegion || null
                setChartIsRegion(value)
                options.chartIsRegion = value
              }
              if (chartToOrder.hasOwnProperty('chartBopa')) {
                const value = chartToOrder?.chartBopa || null
                setChartBopa(value)
                options.chartBopa = value
              }
              if (chartToOrder.hasOwnProperty('chartBopaMode')) {
                const value = chartToOrder?.chartBopaMode || null
                setChartBopaMode(value)
                options.chartBopaMode = value
              }
              if (chartToOrder.hasOwnProperty('npsType')) {
                const value = chartToOrder?.npsType || null
                setChartNpsType(value)
                options.chartNpsType = value
              }
              if (chartToOrder.hasOwnProperty('chartDayStratification')) {
                const value = chartToOrder?.chartDayStratification || null
                setChartDayStratification(value)
                options.chartDayStratification = value
              }
              if (chartToOrder.hasOwnProperty('chartDeliveryReturnType')) {
                const value = chartToOrder?.chartDeliveryReturnType || null
                setChartDeliveryReturnType(value)
                options.chartDeliveryReturnType = value
              }
              if (chartToOrder.hasOwnProperty('npsPerIntervalType')) {
                const value = chartToOrder?.npsPerIntervalType || null
                setChartNpsPerIntervalType(value)
                options.npsPerIntervalType = value
              }
            } catch(e) {}
          }
          let hideHierarchy = false
          let forceHierarchy = false
          if (orderStatusFilter === 'Expiring') {
            setShowHierarchyInitiaror(orderStatusFilter)
          } else {
            setShowHierarchyInitiaror(null)
          }
          if (!chartDate) {
            setChartDate(null)
          } else {
            options.chartDate = chartDate
            if (chartIntervalType) {
              options.chartIntervalType = chartIntervalType
              options.chartEndDate = chartEndDate
            }
            if (!options.npsPerIntervalType) {
              options.showHierarchy = false
              hideHierarchy = true
            } else {
              options.chartEndDate = chartEndDate
            }
          }
          if (options.chartStatus) {
            options.showHierarchy = false
            hideHierarchy = true
          }
          if (options.chartCityOrRegion) {
            options.showHierarchy = false
            hideHierarchy = true
          }
          if (options.chartIsRegion) {
            options.showHierarchy = false
            hideHierarchy = true
          }
          if (hideHierarchy && !isHierarchyBeenClicked && !forceHierarchy) {
            setShowHierarchy(false)
          }
          if (forceHierarchy) {
            setShowHierarchy(true)
          }
          if (chartStatus) {
            closeDetails()
          }

          const isForced = !!((now - orderListUpdater) < 1000)
          const hash = JSON.stringify(options)
          if (!isFetching && isRightDates) {
            if (!isForced) {
              isFetching = true
            }
            setIsOrdersFetching(true)
            fetchingOrdersTO = setTimeout(() => {
              if (isOrdersFetching) {
                setIsOrdersFetching(false)
              }
            }, 60000)
            let orders
            let isHashValid = (now - fetchedOrders[hash]?.date || 0) < (1000 * 60 * 5)
            if (!isForced && fetchedOrders[hash]) {
              orders = fetchedOrders[hash].orders
            } else {
              if (!options.offset) {
                fetchedOrders = {}
              }
              try {
                onAction.send({
                  action: 'Order list request',
                  actionData: {
                    file: 'pages/shop/orders.tsx',
                    requestUrl: '/api/v1/orders',
                    requestData: options,
                  },
                })
                orders = await postData(
                  '/api/v1/orders',
                  options,
                  {
                    currentDealer: currentDealer?.id,
                    locale,
                  },
                )
              } catch(error) {
                setIsOrdersFetching(false)
              }
              if (orders?.error === 'Access forbidden') {
                router.push('/login')
                return
              }
              if (!pageParam) {
                fetchedOrderInfos = {}
              }
              areOrdersFetched = true
              fetchedOrders[hash] = { orders, date: Date.now() }
              setClickedOrder(0)
              preventDevExtraRequestsObject = {}
            }
            isFetching = false
            setOrderStatuses(orders?.statuses || [])
            setIsOrdersFetching(false)
            if (fetchingOrdersTO) {
              clearTimeout(fetchingOrdersTO)
            }
            try {
              document.body.click()
            } catch(e) {}
            resolve(orders)
          } else {
            const lastOrders = fetchedOrders[hash]?.orders
            if (areOrdersFetched) {
              if (lastOrders) {
                resolve(lastOrders)
              } else {
                reject()
              }
            } else {
              reject()
            }
          }
        } else {
          resolve({stop: true})
        }
      } else {
        resolve({})
      }
    })
  }

  const removeChartDateAndStatus = () => {
    try {
      fetchedOrders = {}
      setChartDate(null)
      setChartStatus(null)
      setChartCityOrRegion(null)
      setChartIsRegion(null)
      setClickedOrder(0)
      setChartNpsType(null)
      setChartBopa(null)
      setChartBopaMode(null)
      setChartDayStratification(null)
      setChartDeliveryReturnType(null)
      setChartNpsPerIntervalType(null)
      window.localStorage.removeItem('chartToOrderList')
      datePickerApply()
    } catch (e) {}
  }

  const onOrderCancel = () => {
      fetchedOrders = {}
      setClickedOrder(0)
      // preventDevExtraRequestsObject['getOrderInfo'] = {}
      setTimeout(() => {          // remove it after
        if (datePickerApply) {
          datePickerApply()       // orders.order_data->'_current_date' field update on cancel request
        }
      }, 5000)                    // feature will be implemented
  }

  const isSyntheticDealer = syntheticsDealers.includes(currentDealer?.id)

  const infiniteQueryResult = useInfiniteQuery(
    ORDERS,
    fetchOrders,
    {
      getNextPageParam: lastPage => {
        let nextOffset = 0
        if (lastPage?.count > 0) {
          nextOffset = ((lastPage.offset || 0) + pageCount)
        }
        return nextOffset
      }
    },
  )
  let { data, fetchNextPage, status, error, refetch } = infiniteQueryResult
  if (status === 'error' && !error) {
    if (isOrdersFetching) {
      status = 'loading'
    } else {
      refetch()
    }
  }
  orderListOffset = 0
  data?.pages?.forEach(page => {
    orderListOffset += page.orders?.length || 0
  })
  let tableData = useMemo(() => data?.pages.flatMap(page => page?.orders), [data?.pages]);
  if (data?.pages?.[0]?.stop) {
    tableData = null
  }
  const hasMore = tableData?.length >= pageCount * data?.pages?.length

  let { data: orderData, isLoading: orderIsLoading } = useQuery(
    [ORDER_INFO, { id: selectedOrder.id }],
    async () => {
      return await getOrderInfo()
    },
    {
      onError: (a, b) => {
        addToast(t('Could not fetch selected order'), {
          appearance: 'error',
        })
      },
    },
  )
  if (orderData?.selectedOrder?.id) {
    const index = orderData.selectedOrder.id + '_' + timezoneCode
    if (orderInfos?.[index]?.result) {
      fetchedOrderInfos[orderData.selectedOrder.id] = orderData.selectedOrder
      orderIsLoading = false
    } else {
      // orderIsLoading = orderData?.selectedOrder?.id
    }
  }

  const getOrderInfo = async () => {
    let result = {}
    if (selectedOrder.id) {
      const index = selectedOrder.id + '_' + timezoneCode
      if (!orderInfos[index]) {
        const data = {
          orderId: selectedOrder.id,
          currentDealer,
          router,
          locale,
          timezoneCode,
          browserTimezone,
        }
        orderInfos[index] = new GetOrderInfo(data)
      }
      result = await orderInfos[index].process()
    }
    return result
  }

  const handleDateSelect = (dates: DATES_RANGE) => {
    fetchedOrders = {}
    setClickedOrder(0)
    fetchedOrderInfos = {}
    preventDevExtraRequestsObject = {}
    startDateRange = dates
    setDatesFilter(dates)
  }

  const handleSearch = (searchString) => {
    fetchedOrders = {}
    setClickedOrder(0)
    setSearchString(searchString.replace(/[ ]+/g, ' '))
  }

  const handleStatusFilter = orderStatus => {
    if (orderStatus?.value === 'Expiring') {
      setShowHierarchy(false)
    }
    setClickedOrder(0)
    onAction.send({
        action: 'Status filter',
        actionData: {
          file: 'pages/shop/orders.tsx',
          value: orderStatus?.value || null,
        },
    })
    setOrderStatusFilter(orderStatus?.value || '')
  }

  const handleOrderInfo = async ({
    id,
    ext_key,
    paid,
    service_name,
    index,
  }: Pick<
    ORDER_DETAILS,
    'id' | 'ext_key' | 'paid' | 'service_name' | 'index'
  >) => {
    setSelectedOrder({ id, ext_key, paid, service_name, index })
  	setClickedOrder(id)
    setLastDetailedOrder(id)
    if (fetchedOrderInfos[id]) {
      ordersWithShownItems[id] = !ordersWithShownItems[id]
    } else {
      ordersWithShownItems[id] = true
    }
    setOrdersWithShownItems(ordersWithShownItems)
    if (ordersWithShownItems[id]) {
      showOrdersDetails()
    }
  	if (externalEventCallbacks['OrderItems' + id]) {
  		externalEventCallbacks['OrderItems' + id]('ordersRowClick', ext_key)
  	}
  }

  const handleNewOrder = async () => {
    if (typeof BXLiveChat !== 'undefined') {
      BXLiveChat.close()
    }
    onNewOrder({})
    onAction.send({
        action: 'New order request',
        actionData: {
          file: 'pages/shop/orders.tsx',
          requestUrl: '/api/v1/onNewOrder',
        },
    })
    const result = await postData('/api/v1/onNewOrder', { dealerId: currentDealer.id })
    if (result) {
      let order = {
        dealerId: currentDealer.id,
        currency: result.dealer_currency,
        in_delivery_cost: 0,
        defaultPackageLengthMm: result.defaultPackageLengthMm,
        defaultPackageHeightMm: result.defaultPackageHeightMm,
        defaultPackageWidthMm: result.defaultPackageWidthMm,
        defaultPackageWeightGr: result.defaultPackageWeightGr,
        in_length: result.defaultPackageLengthMm,
        in_height: result.defaultPackageHeightMm,
        in_weight: (result.defaultPackageWeightGr / 1000),
        in_width: result.defaultPackageWidthMm,
        defaultPackageLengthMu: result.defaultPackageLengthMu,
        defaultPackageWidthMu: result.defaultPackageWidthMu,
        defaultPackageHeightMu: result.defaultPackageHeightMu,
        defaultPackageWeightMu: result.defaultPackageWeightMu,
        mu_in_length: result.defaultPackageLengthMu,
        mu_in_width: result.defaultPackageWidthMu,
        mu_in_height: result.defaultPackageHeightMu,
        mu_in_weight: result.defaultPackageWeightMu,
        in_point: "",
        in_total: 1,
        hasDefaultItem: true,
        orderItems: {
          0: {
            _id: 123456,
            line_num: 0,
            sku: '',
            title: "Item",
            weight: result.defaultPackageWeightGr,
            length: result.defaultPackageLengthMm,
            width: result.defaultPackageWidthMm,
            height: result.defaultPackageHeightMm,
            barcode: null,
            quantity: 1,
            price: 1,
            vat: result.VAT,
            total_price: 1,
            _isDefautItem: true,
          },
        },
        paid: true,
        showVat: (result.VAT === 20),
        dealer_dimensions_mu: result.dealer_dimensions_mu,
        dealer_weight_mu: result.dealer_weight_mu,
        dealer_country: result.dealer_country,
        packages: result.packages,
        orderNumber: result.orderNumberObject?.formatted_number,
        isIntact: true,
      }
      if (result.dealerAndChildren) {
        order.dealerAndChildDealers = result.dealerAndChildren
      }
      onNewOrder(order)
    }
  }

  let listColumns = useMemo(() => {
    return allColumns.filter(column => {
      const hasAccessor = (shownColumns.indexOf(column.accessor) !== -1)
      return hasAccessor
    })
  }, [shownColumns])

  const options = orderStatuses.map(item => ({
    ...item,
    label: t(`TEMPStatuses:${item.value}`),
  }))

  const action = {
    onClick: handleNewOrder,
    icon: <Octicon icon={Plus} />,
    label: t('New Order'),
  }

  const dealer_token = currentDealer?.dealer_token

  const backTitle = 'Via.Delivery'

  const links = [
    {
      href: `${process.env.NEXT_PUBLIC_VIADELIVERY_STICKERS_URL}/s/docs/stickers/@${dealer_token}/?lang=${i18n.language}&backUrl=${backUrl}&backTitle=${backTitle}`,
      icon: <Octicon icon={FileSymlinkFile} />,
      label: t('Print labels'),
      dealer_token,
    },
    {
      href: `${process.env.NEXT_PUBLIC_VIADELIVERY_STICKERS_URL}/docs/act/@${dealer_token}/?lang=${i18n.language}`,
      icon: <Octicon icon={FileSymlinkFile} />,
      label: t('Print acts'),
    },
  ]

  useBXWidgetControl()

  const onClickLabel = (clickLabelValue) => {
    setClickLabel(clickLabelValue)
  }

  const closeDetails = (event) => {
    // const rect = orderDetailsCounterpartRef.current?.getBoundingClientRect()
    // if (rect) {
      // const fontSize = parseFloat(window.getComputedStyle(orderDetailsCounterpartRef.current).getPropertyValue('font-size'))
      setOrderDetailsStyle({ display: 'none' })
      setOrdersFrameStyle({ gridTemplateColumns: 'repeat(3, 1fr) 0', gap: 0, oveflowX: 'hidden' })
      localStorage('hideOrdersDetails', true)
      setLastDetailedOrder(0)
    // }
  }
  const showOrdersDetails = () => {
      setOrderDetailsStyle(null)
      setOrdersFrameStyle(null)
      localStorage('hideOrdersDetails', false)
  }

  let tableFrameClassName = styles.ordersTableFrame
  if (!orderData?.selectedOrder && !Object.keys(fetchedOrderInfos || {}).length) {
    if (!orderIsLoading) {
      tableFrameClassName = styles.ordersTableFrame + ' ' + styles.noOrderDetails
    }
  }
  let address = orderData?.selectedOrder?.customer_address?.full_address
  if (!address) {
    address = orderData?.selectedOrder?.manuallyEnteredCustomerAddress
  }
  if (!address) {
    if (orderData?.selectedOrder?.pointAddress && orderData?.selectedOrder?.pointDescription) {
      address = orderData?.selectedOrder?.pointAddress + ', ' + orderData?.selectedOrder?.pointDescription
    }
  }
  let itemsPriceWithDiscount = +(orderData?.selectedOrder?.in_cost) || 0
    itemsPriceWithDiscount += +(orderData?.selectedOrder?.discount_included) || 0
  const isChart = !!(chartStatus || chartNpsType || chartBopa || chartDayStratification || chartDeliveryReturnType || chartNpsPerIntervalType)

  const isOneDay = ((chartDate && !chartIntervalType && !chartNpsPerIntervalType) || chartIntervalType === 'day' || chartNpsPerIntervalType === 'day')
  const isOneDayByIntervalType = ((chartIntervalType || chartNpsPerIntervalType) && datesFilter.startDate === datesFilter.endDate)
  const isOneDayShown = (isOneDay || isOneDayByIntervalType)
  const isRangeShown = (chartIntervalType || chartNpsPerIntervalType) && !isOneDay
  const isChartDateShown = (isOneDayShown || isRangeShown)
  let carrierFilterForExport = null
  if (enCountries.includes(props.currentDealer.dealer_country)) {
    carrierFilterForExport = props.appState.carrierFilter?.toUpperCase()
  }

  return (
    <Layout
      title="orders"
      locale={locale}
      t={t}
      action={action}
      links={dealer_token ? links : []}
      showPickup={true}
      onClickLabel={onClickLabel}
      removeChartDateAndStatus={removeChartDateAndStatus}
      setCurrentPage={setCurrentPage}
      timezoneCode={timezoneCode}
      setTimezoneCode={setTimezoneCode}
      timezoneName={timezoneName}
      setTimezoneName={setTimezoneName}
      onAction={onAction}
    >
      <div className="grid gap-4 grid-cols-4">
        <div className="col-span-3 mt-0 mb-2 flex items-center justify-between">
          <DatePicker
            {...datesFilter}
            onSelect={handleDateSelect}
            show={!isChartDateShown}
            setDatePickerApply={setDatePickerApply}
            timezoneCode={timezoneCode}
            setTimezoneCode={setTimezoneCode}
            timezoneName={timezoneName}
            setTimezoneName={setTimezoneName}
            onAction={onAction}
            saveEndDate={isChart}
            setIsDatePickerSet={setIsDatePickerSet}
            location='ordersPage'
            locale={locale}
            t={t}
          />
          <ChartDate
            show={isChartDateShown}
            chartDate={isOneDayByIntervalType ? datesFilter.startDate : chartDate}
            chartEndDate={chartEndDate}
            chartIntervalType={chartIntervalType}
            chartNpsPerIntervalType={chartNpsPerIntervalType}
            timezoneCode={timezoneCode}
            setTimezoneCode={setTimezoneCode}
            timezoneName={timezoneName}
            setTimezoneName={setTimezoneName}
            locale={locale}
            t={t}
          />
          <div className="flex items-center">
            <div
              className={styles.filters}
            >
              <div className="mr-4">
                <Select
                  name="status-filter"
                  onSelect={handleStatusFilter}
                  options={options}
                  placeholder={t('TEMPStatuses:All statuses')}
                  value={options.find(
                    option => option.id === parseInt(orderStatusFilter),
                  )}
                  show={!isChart}
                />
                <ChartStatus
                  show={isChart}
                  chartStatus={chartStatus}
                  chartCityOrRegion={chartCityOrRegion}
                  chartIsRegion={chartIsRegion}
                  removeChartDateAndStatus={removeChartDateAndStatus}
                  chartNpsType={chartNpsType}
                  chartBopa={chartBopa}
                  chartDayStratification={chartDayStratification}
                  chartDeliveryReturnType={chartDeliveryReturnType}
                  chartNpsPerIntervalType={chartNpsPerIntervalType}
                  t={t}
                />
              </div>
              <CarrierFilter
                show={enCountries.includes(props.currentDealer.dealer_country)}
                appState={props.appState}
                setAppState={props.setAppState}
                t={t}
              />
            </div>
      			<ExportTo
      				t={t}
      				dealerId={currentDealer?.id}
      				dealerName={currentDealer?.name}
      				dates={datesFilter}
      				orderStatus={orderStatusFilter}
      				searchFilter={searchString}
      				shownColumns={shownColumns}
      				access_token={Cookie.get('access_token')}
      				access_realm={Cookie.get('access_realm')}
              chartDate={chartDate}
              chartEndDate={chartEndDate}
              chartIntervalType={chartIntervalType}
              chartStatus={chartStatus}
              chartCityOrRegion={chartCityOrRegion}
              chartIsRegion={chartIsRegion}
              chartNpsType={chartNpsType}
              chartBopa={chartBopa}
              chartBopaMode={chartBopaMode}
              chartDayStratification={chartDayStratification}
              chartDeliveryReturnType={chartDeliveryReturnType}
              chartNpsPerIntervalType={chartNpsPerIntervalType}
              showHierarchy={showHierarchy}
              carrierFilter={carrierFilterForExport}
      				locale={locale}
      				setClickedComponent={setClickedComponent}
      				isOuterClicked={isOuterClicked}
              timezoneCode={timezoneCode}
              onAction={onAction}
              datePickerColumn={datePickerColumn}
              router={router}
      			/>
            <TableSettingsDropdown
              allColumns={allColumns}
              shownColumns={shownColumns}
              setShownColumns={setShownColumns}
              showHierarchy={showHierarchy}
              setShowHierarchy={setShowHierarchy}
              setIsHierarchyBeenClicked={setIsHierarchyBeenClicked}
              isOrdersFetching={isOrdersFetching}
              timezoneCode={timezoneCode}
              setTimezoneCode={setTimezoneCode}
              timezoneName={timezoneName}
              setTimezoneName={setTimezoneName}
              setOrderListUpdater={setOrderListUpdater}
              isHoursAndMinutesShown={isHoursAndMinutesShown}
              setIsHoursAndMinutesShown={setIsHoursAndMinutesShown}
              sortingPickerType={sortingPickerType}
              setSortingPickerType={setSortingPickerType}
              datePickerColumn={datePickerColumn}
              setDatePickerColumn={setDatePickerColumn}
              onAction={onAction}
              t={t}
              clickLabel={clickLabel}
            />
          </div>
        </div>
        <div className="col-span-1 mt-0 mb-2 flex justify-end items-center">
          <SearchInput
            value={searchString}
            onSearch={handleSearch}
            placeholder={t('common:Search')}
            show={!(chartDate || chartCityOrRegion)}
          />
        </div>
      </div>

      <div
        className={styles.ordersFrame}
        style={ordersFrameStyle}
      >
        <div className={tableFrameClassName}>
          <Table
            // @ts-ignore - we can skip that
            columns={listColumns}
            data={tableData}
            t={t}
            emptyState={t('No orders were found')}
            handleRowClick={handleOrderInfo}
            selectedRowIndex={selectedOrder.index}
            locale={locale}
            fetchMoreData={fetchNextPage}
            hasMore={hasMore}
            isLoading={status === 'loading'}
      			order={orderData?.selectedOrder || {}}
      			clickedOrder={clickedOrder}
      			onNewOrder={onNewOrder}
      			onEditOrder={onEditOrder}
      			setOnExternalEventCallback={setOnExternalEventCallback}
            isSyntheticDealer={isSyntheticDealer}
            authDealerId={currentDealer?.id}
            updatePoints={updatePoints}
            currentDealer={currentDealer}
            ordersWithShownItems={ordersWithShownItems}
            setOrdersWithShownItems={setOrdersWithShownItems}
            loading={orderIsLoading}
            fetchedOrderInfos={fetchedOrderInfos}
            isHoursAndMinutesShown={isHoursAndMinutesShown}
            onAction={onAction}
            timezoneCode={timezoneCode}
            isOrderDetailsShown={!!fetchedOrderInfos[lastDetailedOrder]}
            datePickerColumn={datePickerColumn}
            stage={stage}
          />
        </div>
        <div
          className={styles.orderDetailsFrame + (!tableData?.length ? ' ' + styles.hidden : '')}
          style={orderDetailsStyle}
        >
            <OrderDetails
              order={fetchedOrderInfos[lastDetailedOrder] || {}}
              isOrders={tableData?.[0]}
              loading={orderIsLoading}
              locale={locale}
              hint={{ getHint: hint, setHint }}
              timezone={tableData?.[0]?.['dealerTimezone']}
              isSyntheticDealer={isSyntheticDealer}
              onOrderCancel={onOrderCancel}
              onClose={closeDetails}
              ordersWithShownItems={ordersWithShownItems}
              setOrderListUpdater={setOrderListUpdater}
              clickedOrder={clickedOrder}
              authDealerId={currentDealer?.id}
              isHoursAndMinutesShown={isHoursAndMinutesShown}
              onAction={onAction}
              stage={stage}
            />
            {locale === 'ru' && !orderData?.selectedOrder ? (
              <AdBanner />
            ) : (
              ''
            )}
        </div>
        <div
          className="col-span-1"
          ref={orderDetailsCounterpartRef}
        />
    		<div
    			className={styles.hint}
    			style={{
    				top: (hint?.top || 0),
    				left: (hint?.left || 0),
    				display: (hint?.top ? 'flex': 'none'),
    			}}
    		>
    			<div
            className={hint?.isOpenedDates ? styles.isOpenedDates : null}
          >
    				{hint?.value || null}
    			</div>
    		</div>
      </div>
  		<ModalComponent
  			isOpen={isModalComponentOpen}
  			onClose={() => setIsModalComponentOpen(false)}
  			order={editOrder.onEdit ? editOrder : newOrder}
  			editTheOrder={editTheOrder.bind(this)}
  			t={t}
  			addToast={addToast}
  			locale={locale}
        currentDealer={currentDealer}
  			isRu={locale === 'ru'}
  			session={session}
  			dealer_token={dealer_token}
        setUpdatePoints={setUpdatePoints}
        setOrderListUpdater={setOrderListUpdater}
        authDealerId={currentDealer?.id}
        onAction={onAction}
        stage={stage}
  		/>
      <Modal
        title="Delivery Address"
        isOpen={isModalDeliveryOpen}
        setIsOpen={setModalDeliveryIsOpen}
      >
        {orderData?.selectedOrder ? (
          <div style={{ height: '70vh', width: '70vw' }}>
            <Map
              handleMapClose={() => setModalDeliveryIsOpen(false)}
              dealerId={orderData?.selectedOrder.in_shop_id}
              orderCost={itemsPriceWithDiscount}
              delivery_price={orderData?.selectedOrder.in_delivery_cost}
              currency={orderData?.selectedOrder.currency || 'USD'}
              orderLength={Math.round(orderData?.selectedOrder.mu_in_length * 100) / 100}
              orderWidth={Math.round(orderData?.selectedOrder.mu_in_width * 100) / 100}
              orderHeight={Math.round(orderData?.selectedOrder.mu_in_height * 100) / 100}
              orderWeight={Math.round(orderData?.selectedOrder.mu_in_weight * 100) / 100}
              dimUnits={orderData?.selectedOrder.mu_order_dim}
              weightUnits={orderData?.selectedOrder.mu_order_weight}
              address={address}
              pointId={orderData?.selectedOrder.in_point_uuid}
              items_cnt={getItemsCount(orderData?.selectedOrder)}
            />
          </div>
        ) : (
          ''
        )}
      </Modal>
      <Switch
        onChange={setShowHierarchy}
        saveToLocalStorage="isHierarchyShown"
      />
    </Layout>
  )
}

Orders.getInitialProps = async ctx => {
  const { req } = ctx
  const {
    access_token,
    access_realm,
    currentDealer,
    'next-i18next': locale,
  } = cookies(ctx)

  const headers = {
    access_token,
    access_realm,
    currentDealer,
    locale,
  }

  const url = req
    ? `http://${
        process.env.NODE_ENV == 'production'
          ? `127.0.0.1:${process.env.PORT || 8080}`
          : req.headers.host
      }/api/v1/orders`
    : '/api/v1/orders'

  const data = {}
  // data  = await getData(url, headers)
  return {
    ...data,
    namespacesRequired: ['Orders', 'common', 'TEMPStatuses'],
    locale,
    session: '',
  	dealerCurrency: data?.dealerCurrency || 'USD'
  }
}

const localStorage = (key: string, value?: any) => {
  if (typeof window !== 'undefined') {
    try {
      if (value || typeof value === 'boolean') {
        window.localStorage.setItem(key, JSON.stringify(value))
      } else {
        return JSON.parse(window.localStorage.getItem(key))
      }
    } catch(e) {}
  }
}

export default withTranslation(['Orders', 'common', 'TEMPStatuses'])(Orders)
